/**
 * InlineRating Component
 * Simple thumbs up/down rating buttons
 * Non-intrusive, inline design for song lists
 */
import React, { useState } from 'react';
import { authenticatedFetch } from '@/lib/auth';

interface InlineRatingProps {
  trackId: string;
  currentRating?: 'like' | 'dislike' | null;
  onRatingChange?: (rating: 'like' | 'dislike' | null) => void;
}

export const InlineRating: React.FC<InlineRatingProps> = ({
  trackId,
  currentRating: initialRating = null,
  onRatingChange,
}) => {
  const [rating, setRating] = useState<'like' | 'dislike' | null>(initialRating);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRating = async (newRating: 'like' | 'dislike') => {
    if (isSubmitting) return;

    // Toggle off if clicking the same rating
    const finalRating = rating === newRating ? null : newRating;

    setIsSubmitting(true);
    const previousRating = rating;

    // Optimistic update
    setRating(finalRating);
    onRatingChange?.(finalRating);

    try {
      if (finalRating === null) {
        // If toggling off, we might need a delete endpoint
        // For now, just update local state
        return;
      }

      await authenticatedFetch('/api/ratings/rate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          track_id: trackId,
          rating: finalRating
        }),
      });
    } catch (error) {
      console.error('Failed to submit rating:', error);
      // Rollback on error
      setRating(previousRating);
      onRatingChange?.(previousRating);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => handleRating('like')}
        disabled={isSubmitting}
        className={`
          p-1.5 rounded transition-all duration-200 text-lg
          ${rating === 'like'
            ? 'bg-spotify-green/20 text-spotify-green scale-110'
            : 'text-gray-400 hover:text-spotify-green hover:bg-spotify-green/10'
          }
          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        title="Like this track"
        aria-label="Like"
      >
        👍
      </button>
      <button
        onClick={() => handleRating('dislike')}
        disabled={isSubmitting}
        className={`
          p-1.5 rounded transition-all duration-200 text-lg
          ${rating === 'dislike'
            ? 'bg-red-500/20 text-red-400 scale-110'
            : 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'
          }
          ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        title="Dislike this track"
        aria-label="Dislike"
      >
        👎
      </button>
    </div>
  );
};

export default InlineRating;
